import { JobStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { JobAssignmentType } from '~constants/enums';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { trackJobAssignmentCancelled } from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { User } from '~hooks/useUsers/models';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
interface SelectedAssignment {
  type: JobAssignmentType;
  id: string;
}

export const useJobAssignment = (
  jobId: string,
  driver: User | undefined,
  onSuccess?: (job: Job) => void,
  onError?: () => void,
) => {
  const { toasterStore, userStore } = useStores();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { doEvent, assignDriver, assignVendor, unassignDriver } = useJob();
  const companyId = getEffectiveUserCompanyId(userStore);

  const handleAssign = useCallback(
    async (selectedAssignment: SelectedAssignment, complianceOverride?: boolean) => {
      setIsLoading(true);

      try {
        const isVendor = selectedAssignment.type === JobAssignmentType.VENDOR;
        const assignActionResp = isVendor
          ? await assignVendor(
              jobId,
              selectedAssignment.id,
              companyId,
              complianceOverride,
            )
          : await assignDriver(
              jobId,
              selectedAssignment.id,
              companyId,
              complianceOverride,
            );

        if (assignActionResp?.id) {
          AnalyticsController.track('job_assignment_submitted', {
            method: 'job_form',
            assignee_type: isVendor ? 'vendor' : 'driver',
            job_count: 1,
          });
          onSuccess?.(assignActionResp);
          toasterStore.push(
            alert(t('dispatch.job.job_assigned_successfully'), AlertTypes.success),
          );
        }

        setIsLoading(false);
      } catch {
        setIsLoading(false);
        onError?.();
      }
    },
    [assignDriver, assignVendor, jobId, onSuccess, onError, setIsLoading, companyId],
  );

  const handleUnassign = useCallback(async () => {
    setIsLoading(true);

    try {
      const unassignActionResp = driver
        ? await unassignDriver(jobId, companyId)
        : await doEvent(jobId, JobStateEvent.UNASSIGN, companyId);

      if (unassignActionResp?.id) {
        trackJobAssignmentCancelled({
          action: JobStateEvent.UNASSIGN,
          jobIds: [jobId],
          source: 'assignee',
        });
        onSuccess?.(unassignActionResp);
        toasterStore.push(
          alert(t('dispatch.job.job_unassigned_successfully'), AlertTypes.success),
        );
      }

      setIsLoading(false);
    } catch {
      setIsLoading(false);
      onError?.();
    }
  }, [
    unassignDriver,
    doEvent,
    jobId,
    driver,
    onSuccess,
    onError,
    setIsLoading,
    companyId,
  ]);

  return { handleAssign, handleUnassign, isLoading };
};
